import React, { useState, useRef, useEffect } from 'react';
import { useContext } from 'react';
import { LanguageContext } from '../../contexts/LanguageContext';
import { getLanguagesArray, getLanguageConfig } from '../../config/languages';
import styles from '../Header/Header.module.css';

const LanguageDropdown = ({
  iconColor = '#222222',
  textColor = '#222222',
  className = '',
  isMobile = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const dropdownRef = useRef(null);
  const { locale, switchLanguage } = useContext(LanguageContext);

  const languages = getLanguagesArray();
  const currentLanguage = getLanguageConfig(locale);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200); // フェードアウトアニメーションの時間と同じ
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = langCode => {
    switchLanguage(langCode);
    setIsOpen(false);
  };

  if (isMobile) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`${styles.mobileButton} ${textColor === '#ffffff' ? styles.mobileButtonLight : ''}`}
          style={{ color: textColor }}
        >
          <svg
            className={styles.mobileGlobeIcon}
            fill={iconColor}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
          <span className={`text-xs mt-0.5 ${currentLanguage?.fontClass}`}>
            {currentLanguage?.shortName}
          </span>
        </button>

        {shouldRender && (
          <div
            className={`absolute right-0 mt-12 w-max bg-popover rounded-md shadow-lg border border-border z-[100] ${isOpen ? 'animate-fadeIn' : 'animate-fadeOut'}`}
          >
            <div className="py-1">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full whitespace-nowrap text-left px-3 py-2 text-sm hover:bg-accent transition-colors duration-200 flex items-center space-x-2 ${
                    locale === lang.code
                      ? 'bg-primary/20 text-primary font-medium border-l-2 border-primary'
                      : 'text-popover-foreground border-l-2 border-transparent'
                  } ${lang.fontClass}`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                  {locale === lang.code && (
                    <svg
                      className="w-3 h-3 shrink-0 ml-auto"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${styles.langButton} ${className}`}
        style={{
          color: textColor,
        }}
      >
        <div className="flex items-center space-x-2">
          <svg
            className="w-5 h-5"
            fill={iconColor}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
          <span>{currentLanguage?.name}</span>
          <svg
            className={`w-4 h-4 transform transition-all duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill={iconColor}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </div>
      </button>

      {shouldRender && (
        <div
          className={`absolute right-0 mt-2 w-max bg-popover rounded-md shadow-lg border border-border z-[100] ${isOpen ? 'animate-fadeIn' : 'animate-fadeOut'}`}
        >
          <div className="py-1">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full whitespace-nowrap text-left px-4 py-3 text-base hover:bg-accent transition-colors duration-200 flex items-center space-x-3 ${
                  locale === lang.code
                    ? 'bg-primary/20 text-primary font-medium border-l-2 border-primary'
                    : 'text-popover-foreground border-l-2 border-transparent'
                } ${lang.fontClass}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-base">{lang.name}</span>
                {locale === lang.code && (
                  <svg
                    className="w-4 h-4 shrink-0 ml-auto"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
